import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, inject, input, signal } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CustomRouteService } from 'src/app/pages/custom-route/services/custom-route.service';
import { RouteDays } from './interfaces/route-days.interface';
import { normalizeValue } from 'src/app/pages/register-user/utils/value-normalizer';
import { PlannerDayStateService } from 'src/app/pages/custom-route/shared/planner/services/planner-day-state.service';
import { RegisterUserService } from 'src/app/pages/register-user/services/register-user.service';
import { DailyRoute, Point } from 'src/app/pages/custom-route/interfaces/custom-route.interface';

@Component({
  selector: 'app-route-days',
  standalone: true,
  imports: [TranslateModule],
  templateUrl: './route-days.component.html',
  styleUrls: ['./route-days.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class RouteDaysComponent {
  public fromHeaderPlanner = input<boolean>(false);
  
  private _translateService = inject(TranslateService);
  private _customRouteService = inject(CustomRouteService);
  private _registerUserService = inject(RegisterUserService);
  private _plannerDayStateService = inject(PlannerDayStateService);
  
  private _customRouteData = this._customRouteService.customRouteData;

  private dailyCustomRouteData = computed(() => {
    const dayToShowIndex = this._dayToShowIndex();
    const customRouteData = this._customRouteData();
    if (!customRouteData || !customRouteData.dailyRoutes[dayToShowIndex]) {
      return null;
    }
    return customRouteData.dailyRoutes[dayToShowIndex] as DailyRoute;
  })
  
  get dailyPoints(): Point[] {
    return this.dailyCustomRouteData()?.points || [];
  }
  
  private readonly _dayToShowIndex = computed(() => this._plannerDayStateService.dayToShowIndex());
  get dayToShowIndex() {
    return this._dayToShowIndex();
  }
  
  private _numberOfDays = this._registerUserService.numberOfDaysCustomRoute;
  private _currentOrder = this._customRouteService.currentOrder;
  
  get currentOrder() {
    return this._currentOrder();
  }
  
  private _arrayOfDays = signal<string[]>([]);
  public routeDays = signal<RouteDays[]>([]);
  
  setRouteDaysAccordingToNumberOfDays = effect(() => {
    const customRouteData = this._customRouteData();
    if (!customRouteData || !this._numberOfDays()) return;

    this._arrayOfDays.set(Array.from({ length: this._numberOfDays() }, (_, i) => (i + 1).toString()));
    
    this.routeDays.set(this._arrayOfDays()
      .map((day) => {
        const dayNumber = Number(day);
        const dayIndex = dayNumber - 1; // Convertir a índice (0-based)
        
        const numberOfDayTranslated = this._translateService.instant(day);
        const dayWordTranslated = this._translateService.instant('día');
        const dayWordCapitalized = dayWordTranslated.charAt(0).toUpperCase() + dayWordTranslated.slice(1);
        const label = `${dayWordCapitalized} ${numberOfDayTranslated}`;
        
        return {
          id: dayNumber,
          label: label,
          value: normalizeValue(label),
          iconName: this._getWeatherIconForDay(dayIndex)
        };
      })
    );
  }, { allowSignalWrites: true });
  
  private _getWeatherIconForDay(dayIndex: number): string {
    const customRouteData = this._customRouteData();
    if (!customRouteData || !customRouteData.dailyRoutes[dayIndex]) {
      return 'default-weather-icon'; // Icono por defecto
    }
    return customRouteData.dailyRoutes[dayIndex].weatherIconName || 'default-weather-icon';
  }
   
  public handleChipSetRouteDayChange(event: Event) {
    const customEvent = event as CustomEvent<{ values: string[] }>;
    const selectedRouteDayValue = customEvent.detail.values[0]; 
    const selectedRouteDay = Number(selectedRouteDayValue.split('-')[1]); 
    const dayToShowIndex = selectedRouteDay - 1;
    this._plannerDayStateService.setDayToShowIndex(dayToShowIndex);
  }
}